/** Unlink a whole project, every key (spec UI §5). Allowed while any key is Disconnected. */
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import { useOrpc } from '@/_shared/hooks/useOrpc';

import { type LinkedProject, projectLabel } from '../../lib/linkedRepoList';
import { rpcErrorMessage } from './linkedRepoSchema';
import { useInvalidateLinkedRepos } from './useInvalidateLinkedRepos';

export function UnlinkRepositoryDialog({
  project,
  onOpenChange,
  onUnlinked,
}: {
  project: LinkedProject | null;
  onOpenChange: (open: boolean) => void;
  /** After a successful unlink, e.g. to close a drawer showing the project. */
  onUnlinked?: () => void;
}): React.JSX.Element {
  const orpc = useOrpc();
  const invalidate = useInvalidateLinkedRepos();
  const unlink = useMutation(orpc.warehouseProjects.unlinkProject.mutationOptions());
  const pending = unlink.isPending;

  const groupId = project?.groupId ?? null;
  useEffect(() => {
    unlink.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the project changes; the form and mutation are stable
  }, [groupId]);

  const confirm = (): void => {
    if (!project) return;
    unlink.mutate(
      { repoFullName: project.repoFullName, projectPath: project.projectPath },
      {
        onSuccess: () => {
          invalidate();
          toast.success(`Unlinked ${projectLabel(project)}.`);
          onOpenChange(false);
          onUnlinked?.();
        },
      },
    );
  };

  return (
    <Dialog
      open={project !== null}
      onOpenChange={(next) => {
        if (!next && !pending) onOpenChange(false);
      }}
    >
      <DialogContent
        closeDisabled={pending}
        onEscapeKeyDown={(e) => {
          if (pending) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (pending) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="min-w-0 break-words">
            Unlink {project ? projectLabel(project) : 'repository'}?
          </DialogTitle>
          <DialogDescription>
            The agent stops reading it. Docs for any key no other repository uses are deleted.
          </DialogDescription>
        </DialogHeader>
        {unlink.isError && (
          <Callout variant="error">{rpcErrorMessage(unlink.error, 'Could not unlink the repository.')}</Callout>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            disabled={pending}
            onClick={() => onOpenChange(false)}
            label="Cancel"
          />
          <Button
            type="button"
            variant="primary-destructive"
            loading={pending}
            onClick={confirm}
            label={pending ? 'Unlinking…' : 'Unlink'}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
